import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import JsonLd from "@/components/feature/JsonLd";
import { useSeo } from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";
import { getCategory } from "@/mocks/categories";
import ArchiveFilter from "./components/ArchiveFilter";
import ArchiveGrid from "./components/ArchiveGrid";

const PAGE_SIZE = 6;

export default function Archives() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const keyword = searchParams.get("q") ?? "";
  const activeCategory = searchParams.get("category") ?? "all";
  const categoryInfo = activeCategory === "all" ? undefined : getCategory(activeCategory);

  const updateParams = (next: { q?: string; category?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (next.q !== undefined) {
      if (next.q) params.set("q", next.q);
      else params.delete("q");
    }
    if (next.category !== undefined) {
      if (next.category && next.category !== "all") params.set("category", next.category);
      else params.delete("category");
    }
    setSearchParams(params, { replace: true });
    setVisibleCount(PAGE_SIZE);
  };

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    return articles
      .filter((article) => (activeCategory === "all" ? true : article.category === activeCategory))
      .filter((article) => {
        if (!q) return true;
        return (
          article.title.toLowerCase().includes(q) ||
          article.excerpt.toLowerCase().includes(q) ||
          article.tags.some((tag) => tag.toLowerCase().includes(q))
        );
      })
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [keyword, activeCategory]);

  const pageTitle = categoryInfo ? `${categoryInfo.name}｜悬疑档案` : "悬疑档案库｜未解之谜与灵异杂谈合集";

  useSeo({
    title: `${pageTitle}｜猎奇吃瓜`,
    description: categoryInfo
      ? `猎奇吃瓜${categoryInfo.name}栏目：${categoryInfo.desc}`
      : "浏览猎奇吃瓜全部悬疑档案，覆盖未解之谜、灵异杂谈、都市传说、超自然现象、悬疑档案与暗网传闻，支持关键词搜索与题材筛选。",
    keywords: categoryInfo
      ? `猎奇吃瓜,${categoryInfo.name},未解之谜,灵异杂谈,悬疑档案`
      : "猎奇吃瓜,悬疑档案,未解之谜,灵异杂谈,都市传说,超自然现象,暗网传闻",
    canonical: activeCategory === "all" ? "/archives" : `/archives?category=${activeCategory}`,
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-950">
      <SiteHeader />
      <main className="flex-1">
        <section className="border-b border-background-800 bg-background-900/60">
          <div className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-14">
            <Breadcrumbs
              items={[
                { label: "首页", to: "/" },
                { label: "悬疑档案", to: activeCategory === "all" ? undefined : "/archives" },
                ...(categoryInfo ? [{ label: categoryInfo.name }] : []),
              ]}
            />
            <p className="mt-5 text-xs tracking-[0.3em] text-primary-400">ARCHIVE LIBRARY</p>
            <h1 className="mt-3 font-heading text-3xl font-black text-foreground-50 md:text-4xl">
              {categoryInfo ? categoryInfo.name : "悬疑档案库"}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-400 md:text-base">
              {categoryInfo
                ? categoryInfo.desc
                : "这里汇集了猎奇吃瓜全部已归档的内容。你可以用关键词检索标题、摘要与标签，也可以按题材逐个翻阅——每个分类下都保留着完整的线索与时间线。"}
            </p>
          </div>
        </section>

        <section className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-14">
          <ArchiveFilter
            keyword={keyword}
            onKeywordChange={(value) => updateParams({ q: value })}
            activeCategory={activeCategory}
            onCategoryChange={(value) => updateParams({ category: value })}
            total={filtered.length}
          />

          <div className="mt-8">
            <ArchiveGrid
              articles={filtered}
              visibleCount={visibleCount}
              onLoadMore={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
            />
          </div>

          <div className="mt-12 rounded-lg border border-background-800 bg-background-900 p-6 md:p-8">
            <h2 className="font-heading text-lg font-bold text-foreground-50">没找到想要的题材？</h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-400">
              我们每周都会新增档案。你可以告诉我们想看的案件类型，或者直接提供一条线索，编辑部会优先处理。
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to="/faq"
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-400"
              >
                <i className="ri-question-line" /> 常见问题
              </Link>
              <Link
                to="/about"
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-5 py-2.5 text-sm text-foreground-200 transition-colors hover:border-primary-400 hover:text-primary-300"
              >
                <i className="ri-mail-send-line" /> 提供线索
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />

      <JsonLd
        id="archives-collection"
        data={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: categoryInfo ? categoryInfo.name : "悬疑档案库",
          description: categoryInfo ? categoryInfo.desc : "猎奇吃瓜全部悬疑档案合集。",
          url: "https://dioxy.cn/archives",
          inLanguage: "zh-CN",
          mainEntity: {
            "@type": "ItemList",
            itemListElement: filtered.slice(0, 10).map((article, index) => ({
              "@type": "ListItem",
              position: index + 1,
              url: `https://dioxy.cn/article/${article.slug}`,
              name: article.title,
            })),
          },
        }}
      />
      <JsonLd
        id="archives-breadcrumb"
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://dioxy.cn/" },
            { "@type": "ListItem", position: 2, name: "悬疑档案", item: "https://dioxy.cn/archives" },
            ...(categoryInfo
              ? [
                  {
                    "@type": "ListItem",
                    position: 3,
                    name: categoryInfo.name,
                    item: `https://dioxy.cn/archives?category=${categoryInfo.slug}`,
                  },
                ]
              : []),
          ],
        }}
      />
    </div>
  );
}